import { HttpContext, HttpRequest } from "@angular/common/http";

/**
 * environments
 */
import { environment } from "src/environments/environment";

/**
 * consts
 */
import { 
    BYPASS_APP_REQUEST_CONTEXT, 
    BYPASS_JWT_CONTEXT, 
    LOADING_SHOW_CONTEXT,
    BYPASS_ERROR_RESPONSE_CONTEXT, 
    BYPASS_ERROR_401_RESPONSE_CONTEXT,
    BYPASS_TOAST_CONTEXT,
    TOAST_MESSAGE_CONTEXT,
    TOAST_FORCE_CONTEXT,
    TOAST_MESSAGE_KEY_CONTEXT,
} from "../index";

/**
 * interfaces
 */
import { IToastMsgHttpContext } from "../interceptors/http/toastr/interfaces/toast-msg-http-context";

/**
 * App HTTP Utilities
 * @class AppHttpUtils
 * @description Provides utility methods for HTTP request validation and processing.
 * Contains static methods for checking request types, URL validation, and request filtering.
 * 
 * @example
 * ```typescript
 * // Check if URL has host
 * const hasHost = AppHttpUtils.isHasAppHost('https://api.example.com');
 * 
 * // Check if request is for i18n
 * const isI18n = AppHttpUtils.isIn18nRequest('/i18n/en.json');
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export abstract class AppHttpUtils {

    /**
     * Checks if an HTTP request is an app request
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request is an app request
     * @description Determines if the request is an app request.
     * Returns true if the request URL includes the app host.
     * 
     * @example
     * ```typescript
     * const isAppRequest = AppHttpUtils.isAppRequest(request);
     * ```
     */
    public static isAppRequest(req: HttpRequest<unknown>): boolean {
        return req.url.includes(environment.APP_HOST);
    }

    /**
     * Checks if a URL contains a host (protocol)
     * @public
     * @static
     * @param {string} url - The URL to check
     * @returns {boolean} True if URL has http:// or https:// protocol
     * @description Validates if the provided URL string contains a protocol prefix.
     * Returns false for relative URLs or invalid input.
     */
    public static isHasHost(url: string): boolean {
        
        // Check if url is a string and has a host
        if (!url || typeof url !== 'string') return false;

        // Check if url has a host
        return url.startsWith('http://') || url.startsWith('https://');
    }

    /**
     * Checks if a URL is an internationalization request
     * @public
     * @static
     * @param {string} url - The URL to check
     * @returns {boolean} True if URL contains '/i18n/' path
     * @description Determines if the URL is requesting internationalization files.
     * Used by interceptors to identify translation requests.
     */
    public static isIn18nRequest(url: string): boolean {
        
        // Check if url is a string and has a i18n endpoint
        if (!url || typeof url !== 'string') return false;

        // Check if url is an i18n request
        return url.includes('/i18n/');
    }

    /**
     * Checks if an HTTP request should show loading
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request should show loading
     * @description Determines if the request should show loading.
     * Returns true if the request has the loading show context set to true, indicating
     * that loading should be shown for this request, otherwise false.
     */
    public static isShowLoading(req: HttpRequest<unknown>): boolean {
        return req.context.get(LOADING_SHOW_CONTEXT) === true;
    }

    /**
     * Checks if an HTTP request should be processed by app interceptors
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request should be processed by app interceptors
     * @description Determines if the request should bypass app-specific processing.
     * Returns true if request has bypass app request context set.
     */
    public static isHasBypassAppRequest(req: HttpRequest<unknown>): boolean {
        return req.context.get(BYPASS_APP_REQUEST_CONTEXT) === true;
    }

    /**
     * Checks if an HTTP request has the JWT bypass context
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request has JWT bypass context set
     * @description Determines if the request should bypass JWT authentication processing.
     * Returns true if the request contains the JWT bypass context, indicating
     * that JWT token validation should be skipped for this request.
     */
    public static isHasBypassJwt(req: HttpRequest<unknown>): boolean {
        return req.context.get(BYPASS_JWT_CONTEXT) === true;
    }

    /**
     * 
     * @param {HttpContext} ctx - The HTTP context to set the JWT bypass context on
     * @returns {HttpContext} The HTTP context with the JWT bypass context set
     * @description Sets the JWT bypass context on the HTTP context.
     * Returns the HTTP context with the JWT bypass context set.
     * 
     * @example
     * ```typescript
     * const ctx = new HttpContext();
     * const newCtx = AppHttpUtils.withBypassJwt(ctx);
     * ```
     */
    public static withBypassJwt(ctx = new HttpContext()): HttpContext {
        return ctx.set(BYPASS_JWT_CONTEXT, true);
    }

    /**
     * Checks if an HTTP request has the error response bypass context
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request has error response bypass context set
     * @description Determines if the request should bypass error response processing.
     * Returns true if the request contains the error response bypass context, indicating
     * that error response processing should be skipped for this request.
     */
    public static isHasBypassErrorResponse(req: HttpRequest<unknown>): boolean {
        return req.context.get(BYPASS_ERROR_RESPONSE_CONTEXT) === true;
    }

    /**
     * 
     * @param {HttpContext} ctx - The HTTP context to set the error response bypass context on
     * @returns {HttpContext} The HTTP context with the error response bypass context set
     * @description Sets the error response bypass context on the HTTP context.
     * Returns the HTTP context with the error response bypass context set.
     * 
     * @example
     * ```typescript
     * const ctx = new HttpContext();
     * const newCtx = AppHttpUtils.withBypassErrorResponse(ctx);
     * ```
     */
    public static withBypassErrorResponse(ctx = new HttpContext()): HttpContext {
        return ctx.set(BYPASS_ERROR_RESPONSE_CONTEXT, true);
    }

    /**
     * Checks if an HTTP request has the error 401 response bypass context
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request has error 401 response bypass context set
     * @description Determines if the request should bypass error 401 response processing.
     * Returns true if the request contains the error 401 response bypass context, indicating
     * that error 401 response processing should be skipped for this request.
     */
    public static isHasBypassError401Response(req: HttpRequest<unknown>): boolean {
        return req.context.get(BYPASS_ERROR_401_RESPONSE_CONTEXT) === true;
    }
    
    /**
     * Checks if an HTTP request has the toast bypass context
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request has toast bypass context set
     * @description Determines if the request should bypass toast processing.
     * Returns true if the request contains the toast bypass context, indicating
     * that toast processing should be skipped for this request.
     */
    public static isHasBypassToast(req: HttpRequest<unknown>): boolean {
        return req.context.get(BYPASS_TOAST_CONTEXT) === true;
    }

    /**
     * 
     * @param {HttpContext} ctx - The HTTP context to set the toast bypass context on
     * @returns {HttpContext} The HTTP context with the toast bypass context set
     * @description Sets the toast bypass context on the HTTP context.
     * Returns the HTTP context with the toast bypass context set.
     * 
     * @example
     * ```typescript
     * const ctx = new HttpContext();
     * const newCtx = AppHttpUtils.withBypassToast(ctx);
     * ```
     */
    public static withBypassToast(ctx = new HttpContext()): HttpContext {
        return ctx.set(BYPASS_TOAST_CONTEXT, true);
    }    

    /**
     * Checks if an HTTP request has the toast force context
     * @public
     * @static
     * @param {HttpRequest<unknown>} req - The HTTP request to check
     * @returns {boolean} True if request has toast force context set
     * @description Determines if the request should force show toast.
     * Returns true if the request contains the toast force context, indicating
     * that toast should be forced to show for this request.
     */
    public static isHasToastForce(req: HttpRequest<unknown>): boolean {
        return req.context.get(TOAST_FORCE_CONTEXT) === true;
    }

    /**
     * Sets the toast messages on the HTTP context
     * @public
     * @static
     * @param {IToastMsgHttpContext | null} messages - The toast messages to set
     * @param {HttpContext} ctx - The HTTP context to set the toast messages on
     * @returns {HttpContext} The HTTP context with the toast messages set
     * @description Sets the toast messages on the HTTP context.
     * Returns the HTTP context with the toast messages set.
     * 
     * @example
     * ```typescript
     * const ctx = new HttpContext();
     * const messages = { message: 'Error', title: 'Warning', type: 'info' };
     * const newCtx = AppHttpUtils.withToastMessages(messages, ctx);
     * ```
     */
    public static withToastMessages(messages: IToastMsgHttpContext, ctx = new HttpContext()): HttpContext {
        return ctx.set(TOAST_MESSAGE_CONTEXT, messages);
    }

    /**
     * 
     * @param ctx - The HTTP context to get the toast messages from
     * @returns {IToastMsgHttpContext | null} The toast messages
     * @description Gets the toast messages from the HTTP context.
     * Returns the toast messages from the HTTP context.
     * 
     * @example
     * ```typescript
     * const ctx = new HttpContext();
     * const messages = AppHttpUtils.withToastMessages({ message: 'Error', title: 'Warning', type: 'info' }, ctx);
     * const messages = AppHttpUtils.getToastMessages(ctx) as IToastMsgHttpContext | null;
     * ```
     */
    public static getToastMessages(ctx: HttpContext): IToastMsgHttpContext | null {
        return ctx.get(TOAST_MESSAGE_CONTEXT) ?? null;
    }

    /**
     * Names the response field the toast message is read from
     * @public
     * @static
     * @param {string} key - Field of the response `data` object holding the
     * message, as a single key or a dot-path into nested data
     * @param {HttpContext} ctx - The HTTP context to set the key on
     * @returns {HttpContext} The HTTP context with the toast message key set
     * @description Use when the server words the message itself but returns it
     * beside other data, so it cannot be picked up as a bare `data` string.
     * A message set through `withToastMessages` wins over this.
     *
     * @example
     * ```typescript
     * const ctx = AppHttpUtils.withToastMessageKey('message');
     * const nested = AppHttpUtils.withToastMessageKey('parent.batch.msg');
     * ```
     */
    public static withToastMessageKey(key: string, ctx = new HttpContext()): HttpContext {
        return ctx.set(TOAST_MESSAGE_KEY_CONTEXT, key);
    }

    /**
     *
     * @param {HttpContext} ctx - The HTTP context to get the toast message key from
     * @returns {string | null} The toast message key
     * @description Gets the field name the toast message should be read from.
     * Returns null when the request did not set one.
     *
     * @example
     * ```typescript
     * const key = AppHttpUtils.getToastMessageKey(req.context);
     * ```
     */
    public static getToastMessageKey(ctx: HttpContext): string | null {
        return ctx.get(TOAST_MESSAGE_KEY_CONTEXT) ?? null;
    }
}
